import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, TrendingDown, ExternalLink, Copy, Star } from 'lucide-react';
import { getCoinById } from '../utils/mockDataGenerator';
import { CoinData } from '../data/coinList';
import PriceChart from '../components/coin/PriceChart';
import CoinStats from '../components/coin/CoinStats';
import MarketInfo from '../components/coin/MarketInfo';
import AboutCoin from '../components/coin/AboutCoin';

const CoinDetailPage: React.FC = () => {
  const { coinId } = useParams<{ coinId: string }>();
  const navigate = useNavigate();
  const [coin, setCoin] = useState<CoinData | null>(null);
  const [timeframe, setTimeframe] = useState('7d');

  useEffect(() => {
    if (coinId) {
      const coinData = getCoinById(coinId);
      setCoin(coinData);
    }
  }, [coinId]);

  if (!coin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <div className="text-center py-10 sm:py-20">
          <h2 className="text-2xl font-bold mb-4">Coin not found</h2>
          <button
            onClick={() => navigate('/')}
            className="text-violet-500 hover:text-violet-400 transition-colors"
          >
            Return to home
          </button>
        </div>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    if (price >= 1) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (price >= 0.01) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(8)}`;
  };

  const formatMarketCap = (num: number) => {
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toLocaleString()}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
      {/* Breadcrumb */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center space-x-2 text-sm text-zinc-400 mb-6"
      >
        <button
          onClick={() => navigate('/')}
          className="flex items-center space-x-1 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Markets</span>
        </button>
        <span>/</span>
        <span>{coin.name}</span>
      </motion.div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6">
          <div className="flex items-start space-x-3 mb-4 sm:mb-0">
            <img src={coin.image} alt={coin.name} className="w-12 h-12 rounded-full flex-shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center flex-wrap gap-2">
                <h1 className="text-xl sm:text-3xl font-bold">{coin.name}</h1>
                <span className="text-sm sm:text-lg text-zinc-400">{coin.symbol.toUpperCase()}</span>
                <span className="px-2 py-0.5 bg-zinc-800 rounded text-xs">#{coin.rank}</span>
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <button className="flex items-center space-x-1 text-zinc-400 hover:text-white transition-colors px-2 py-1 bg-zinc-900/50 rounded text-xs">
                  <Star className="w-3 h-3" />
                  <span>Watch</span>
                </button>
                <button className="flex items-center space-x-1 text-zinc-400 hover:text-white transition-colors px-2 py-1 bg-zinc-900/50 rounded text-xs">
                  <Copy className="w-3 h-3" />
                  <span>Copy</span>
                </button>
                <a href="https://example.com" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 text-zinc-400 hover:text-white transition-colors px-2 py-1 bg-zinc-900/50 rounded text-xs">
                  <ExternalLink className="w-3 h-3" />
                  <span>Website</span>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-4 sm:mt-0 sm:text-right">
            <div className="text-2xl sm:text-3xl font-bold mb-1">{formatPrice(coin.current_price)}</div>
            <div className="flex items-center space-x-1">
              {coin.price_change_percentage_24h >= 0 ? (
                <TrendingUp className="w-5 h-5 text-green-500" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-500" />
              )}
              <span className={`text-base sm:text-lg font-medium ${
                coin.price_change_percentage_24h >= 0 ? 'text-green-500' : 'text-red-500'
              }`}>
                {Math.abs(coin.price_change_percentage_24h).toFixed(2)}%
              </span>
              <span className="text-zinc-400 text-sm">(24h)</span>
            </div>
          </div>
        </div>

        {/* Quick Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          <div className="glass-effect rounded-lg p-3 sm:p-4">
            <p className="text-xs text-zinc-500 mb-1">Market Cap</p>
            <p className="text-sm sm:text-lg font-semibold">{formatMarketCap(coin.market_cap)}</p>
          </div>
          <div className="glass-effect rounded-lg p-3 sm:p-4">
            <p className="text-xs text-zinc-500 mb-1">24h Volume</p>
            <p className="text-sm sm:text-lg font-semibold">{formatMarketCap(coin.total_volume)}</p>
          </div>
          <div className="glass-effect rounded-lg p-3 sm:p-4">
            <p className="text-xs text-zinc-500 mb-1">Circulating Supply</p>
            <p className="text-sm sm:text-lg font-semibold">{(coin.circulating_supply / 1e6).toFixed(2)}M</p>
          </div>
          <div className="glass-effect rounded-lg p-3 sm:p-4">
            <p className="text-xs text-zinc-500 mb-1">Max Supply</p>
            <p className="text-sm sm:text-lg font-semibold">
              {coin.max_supply ? `${(coin.max_supply / 1e6).toFixed(2)}M` : '∞'}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Price Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="glass-effect rounded-xl p-4 sm:p-6"
          >
            <PriceChart coin={coin} timeframe={timeframe} setTimeframe={setTimeframe} />
          </motion.div>

          {/* About Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="glass-effect rounded-xl p-4 sm:p-6"
          >
            <AboutCoin coin={coin} />
          </motion.div>
        </div>

        <div className="space-y-6">
          {/* Coin Statistics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="glass-effect rounded-xl p-4 sm:p-6"
          >
            <CoinStats coin={coin} />
          </motion.div>

          {/* Market Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="glass-effect rounded-xl p-4 sm:p-6"
          >
            <MarketInfo coin={coin} />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CoinDetailPage;